import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ref, getDatabase, get, update, set, push } from "firebase/database";
import { toastrError } from "../components/toastr";
import { v4 } from "uuid";
import CountryAutocomplete from "../components/UI/countryAutocomplete";

const inputClass =
  "w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 outline-none text-sm text-gray-900 placeholder-gray-400 focus:border-primary";

// ─── Step 1 ───────────────────────────────────────────────────────────────────
function Step1({ data, onChange }) {
  return (
    <div className="flex flex-col gap-y-4">
      <div>
        <label className="text-xs font-medium text-gray-500 mb-1 block">
          Business Name
        </label>
        <input
          value={data.companyName}
          name="companyName"
          className={inputClass}
          placeholder="e.g. The Golden Fork"
          onChange={onChange}
        />
      </div>
      <div>
        <label className="text-xs font-medium text-gray-500 mb-1 block">
          Country
        </label>
        {/* CountryAutocomplete dropdown uses z-50 — renders above the fixed CTA (z-40) */}
        <CountryAutocomplete
          value={data.country}
          name="country"
          className={inputClass}
          onChange={onChange}
          placeholder="Search your country…"
        />
      </div>
    </div>
  );
}

// ─── Step 2 ───────────────────────────────────────────────────────────────────
function Step2({ tables, setTables }) {
  const [tableNum, setTableNum] = useState("");
  const [tableLabel, setTableLabel] = useState("");
  const [tableCapacity, setTableCapacity] = useState("");

  function addTable() {
    if (!tableNum.trim()) return;
    setTables([
      ...tables,
      {
        id: v4(),
        number: tableNum.trim(),
        label: tableLabel.trim(),
        capacity: tableCapacity ? parseInt(tableCapacity, 10) : null,
        status: "free",
        currentCustomerId: null,
      },
    ]);
    setTableNum("");
    setTableLabel("");
    setTableCapacity("");
  }

  function removeTable(id) {
    setTables(tables.filter((t) => t.id !== id));
  }

  return (
    <div className="flex flex-col gap-y-4">
      <p className="text-sm text-gray-500">
        Add your tables so staff can assign customers when they check in. You
        can always add or change these later in Settings.
      </p>

      <div className="flex flex-col gap-y-2">
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Table Number *
            </label>
            <input
              value={tableNum}
              onChange={(e) => setTableNum(e.target.value)}
              className="w-full px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-200 text-sm outline-none focus:border-primary"
              placeholder="e.g. 1"
            />
          </div>
          <div className="w-20">
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Seats
            </label>
            <input
              value={tableCapacity}
              onChange={(e) => setTableCapacity(e.target.value)}
              className="w-full px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-200 text-sm outline-none focus:border-primary"
              placeholder="4"
              type="number"
              min="1"
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">
            Label{" "}
            <span className="text-gray-400">(optional — e.g. Window, Patio, VIP)</span>
          </label>
          <input
            value={tableLabel}
            onChange={(e) => setTableLabel(e.target.value)}
            className="w-full px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-200 text-sm outline-none focus:border-primary"
            placeholder="e.g. Window seat or VIP"
          />
        </div>
      </div>

      <button
        onClick={addTable}
        disabled={!tableNum.trim()}
        className={`w-full py-2.5 rounded-xl text-sm font-semibold ${
          tableNum.trim()
            ? "bg-primary text-white"
            : "bg-gray-100 text-gray-400 cursor-not-allowed"
        }`}
      >
        + Add Table
      </button>

      {tables.length > 0 && (
        <div className="flex flex-col gap-y-2">
          {tables.map((t) => (
            <div
              key={t.id}
              className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-xl px-4 py-3"
            >
              <div>
                <span className="text-sm font-semibold text-gray-800">
                  Table {t.number}
                </span>
                {t.label && (
                  <span className="text-xs text-gray-500 ml-2">{t.label}</span>
                )}
                {t.capacity && (
                  <span className="text-xs text-gray-400 ml-2">
                    · {t.capacity} seats
                  </span>
                )}
              </div>
              <button
                onClick={() => removeTable(t.id)}
                className="text-red-400 text-xs font-medium"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Progress dots ─────────────────────────────────────────────────────────────
function StepDots({ step }) {
  return (
    <div className="flex items-center gap-2 justify-center mt-5">
      {[1, 2].map((s, i) => (
        <React.Fragment key={s}>
          <div
            className={`rounded-full transition-all duration-300 ${
              s === step
                ? "w-5 h-2 bg-primary"
                : s < step
                ? "w-2 h-2 bg-primary/40"
                : "w-2 h-2 bg-gray-200"
            }`}
          />
          {i < 1 && (
            <div
              className={`w-8 h-0.5 ${s < step ? "bg-primary" : "bg-gray-200"}`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────────
function Onboarding() {
  const navigate = useNavigate();
  const email = localStorage.getItem("email");
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ companyName: "", country: "" });
  const [tables, setTables] = useState([]);

  function handleChange(e) {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  }

  async function finish() {
    setSaving(true);
    try {
      const database = getDatabase();
      const userInfoRef = ref(database, "userInfo");
      const snapshot = await get(userInfoRef);

      let ownerId;

      if (snapshot.exists()) {
        const allData = snapshot.val();
        const userKey = Object.keys(allData).find(
          (k) => allData[k].email === email
        );
        if (userKey) {
          ownerId = allData[userKey].id;
          if (!ownerId) {
            ownerId = v4();
            await update(ref(database, `userInfo/${userKey}`), {
              id: ownerId,
              companyName: formData.companyName,
              country: formData.country,
              onboardingComplete: true,
            });
          } else {
            await update(ref(database, `userInfo/${userKey}`), {
              companyName: formData.companyName,
              country: formData.country,
              onboardingComplete: true,
            });
          }
        }
      }

      if (!ownerId) {
        ownerId = v4();
        await push(ref(database, "userInfo"), {
          email,
          id: ownerId,
          userType: "free",
          companyName: formData.companyName,
          country: formData.country,
          onboardingComplete: true,
        });
      }

      localStorage.setItem("ownerId", ownerId);

      if (tables.length > 0) {
        for (const table of tables) {
          await set(ref(database, `tables/${ownerId}/${table.id}`), table);
        }
      }

      localStorage.setItem("onboardingComplete", "true");
      navigate("/welcome");
    } catch (err) {
      toastrError("Could not complete setup. Please try again.");
      setSaving(false);
    }
  }

  const stepTitles = ["About your business", "Set up your tables"];
  const step1Valid = formData.companyName.trim() && formData.country;

  return (
    <>
      {/*
        Layout: min-h-screen (normal flow, not fixed height).
        pb-28 reserves space at the bottom so content never sits behind the fixed CTA bar.
        This avoids all keyboard-resize issues — the page simply scrolls naturally when
        the keyboard opens, and the fixed CTA (z-40) stays above the keyboard on Android.
        The country dropdown (z-50) renders above the fixed CTA (z-40) so it's never covered.
      */}
      <div className="min-h-screen bg-white pb-28">

        {/* ── Header ── */}
        <div className="text-center px-5 pt-8 pb-5">
          <img
            src="logo.png"
            alt="WaitLessQ"
            className="w-12 h-12 rounded-2xl mx-auto mb-3"
          />
          <h1 className="text-xl font-bold text-gray-900">WaitLessQ</h1>
          <p className="text-sm text-gray-500 mt-1">Let's set up your business</p>
          <StepDots step={step} />
        </div>

        {/* ── Card ── */}
        <div className="px-5">
          <div className="w-full max-w-sm mx-auto bg-white rounded-2xl border border-gray-100 shadow-card p-6">
            <h2 className="text-base font-bold text-gray-900 mb-0.5">
              {stepTitles[step - 1]}
            </h2>
            <p className="text-xs text-gray-400 mb-5">Step {step} of 2</p>

            {step === 1 && <Step1 data={formData} onChange={handleChange} />}
            {step === 2 && <Step2 tables={tables} setTables={setTables} />}
          </div>
        </div>

      </div>

      {/*
        Fixed CTA — z-40 keeps it below the country dropdown (z-50).
        On Android: the fixed bar anchors to the visual viewport (above keyboard),
        so the button stays visible while typing.
        On iOS: keyboard overlays the button temporarily while typing — acceptable,
        since the user taps Continue only after finishing input.
      */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white px-5 pt-3 pb-8 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]">
        <div className="w-full max-w-sm mx-auto">
          {step === 1 && (
            <button
              onClick={() => setStep(2)}
              disabled={!step1Valid}
              className={`w-full py-3.5 rounded-xl font-semibold text-sm ${
                step1Valid
                  ? "bg-primary text-white"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
            >
              Continue
            </button>
          )}
          {step === 2 && (
            <div className="flex gap-3">
              <button
                onClick={finish}
                disabled={saving}
                className="flex-1 py-3.5 rounded-xl font-semibold text-sm border border-gray-200 text-gray-600 disabled:opacity-50"
              >
                Skip for now
              </button>
              <button
                onClick={finish}
                disabled={saving}
                className="flex-1 py-3.5 rounded-xl font-semibold text-sm bg-primary text-white disabled:opacity-60"
              >
                {saving
                  ? "Setting up…"
                  : tables.length > 0
                  ? "Save & Continue"
                  : "Continue"}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Onboarding;
